// Outbound Caller → Settings. Opened from the footer's Settings tab; each row
// leads to one settings area. The status card reuses the account-wide on/off
// toggle that the agent and leads headers show.
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./OutboundSettings.module.css";
import OutboundCallerBrand from "../OutboundCallerBrand/OutboundCallerBrand";
import HeaderBar from "../HeaderBar/HeaderBar";
import PounceToggle from "../PounceLeadsPage/PounceToggle/PounceToggle";
import PounceConnectorsModal from "../FastAgentDetails/PounceConnectorsModal/PounceConnectorsModal";
import PounceFooterNav from "../PounceAgentDetail/PounceFooterNav/PounceFooterNav";
import usePounceAccess, { POUNCE_LOCK_MESSAGE } from "../../hooks/usePounceAccess";
import { useDashboardStore } from "../../Store/agentZustandStore";
import { getAgentById } from "../../Store/apiStore";

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

const LinkIcon = () => (
  <svg {...svgProps}>
    <path d="M10 13a5 5 0 0 0 7.1 0l3-3a5 5 0 0 0-7.1-7.1l-1.7 1.7" />
    <path d="M14 11a5 5 0 0 0-7.1 0l-3 3A5 5 0 0 0 11 21.1l1.7-1.7" />
  </svg>
);

const PhoneIcon = () => (
  <svg {...svgProps}>
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
  </svg>
);

const BoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" />
  </svg>
);
const BotIcon = () => (
  <svg {...svgProps}>
    <path d="M12 8V4H8" />
    <rect width="16" height="12" x="4" y="8" rx="2" />
    <path d="M2 14h2" />
    <path d="M20 14h2" />
    <path d="M15 13v2" />
    <path d="M9 13v2" />
  </svg>
);

const Chevron = () => (
  <svg className={styles.chevron} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const OutboundSettings = () => {
  const navigate = useNavigate();
  const { canEdit, canManage } = usePounceAccess();
  // null = not known yet. PounceToggle keeps this cache in step with the server.
  const enabled = useDashboardStore((s) => s.pounceEnabled);
  const [showSources, setShowSources] = useState(false);
  const routingBusy = useRef(false);
  const agentId = sessionStorage.getItem("SelectAgentId") || "";
  const live = enabled === true;

  // `allowed` follows the same team rules as the rest of the Outbound Caller
  // pages; the server enforces them too.
  const rows = [
    {
      key: "sources",
      title: "Lead Sources",
      desc: "Connect and test where your leads come from",
      Icon: LinkIcon,
      tone: styles.iconPurple,
      allowed: canManage,
      onClick: () => setShowSources(true),
    },
    {
      key: "routing",
      title: "Call Routing",
      desc: "Transfer calls to your team when needed",
      Icon: PhoneIcon,
      tone: styles.iconPurple,
      allowed: canEdit,
      onClick: async () => {
        // CallTransfer sessionStorage se agent padhta hai — purana receptionist na rahe
        if (routingBusy.current) return; // double tap pe do request nahi
        routingBusy.current = true;
        try {
          const { data: agent } = await getAgentById(agentId);
          sessionStorage.setItem("agentDetails", JSON.stringify({ agent }));
          sessionStorage.setItem("SelectAgentBusinessId", agent?.businessId || "");
          navigate("/call-transfer", { state: { returnTo: "/outbound-settings" } });
        } catch (err) {
          console.error("Call Routing: agent load failed", err);
        } finally {
          routingBusy.current = false;
        }
      },
    },
    {
      key: "calling",
      title: "Agent Settings",
      desc: "Sensitivity, call recording declaration, and other agent preferences",
      Icon: BotIcon,
      tone: styles.iconGreen,
      allowed: canEdit,
      onClick: () => navigate("/call-setting"),
    },
    {
      key: "billing",
      title: "Usage & Billing",
      desc: "Manage your plan, usage and billing details",
      Icon: BoltIcon,
      tone: styles.iconSolid,
      allowed: canManage,
      onClick: () => navigate("/outbound-billing"),
    },
  ];

  return (
    <div className={styles.page}>
      {/* Usage & Billing / Invoices jaisa hi HeaderBar: "Outbound Caller" bada
          title, page ka naam neeche h6 me (.header h6 rule). */}
      <div className={styles.header}>
        <HeaderBar
          title="Outbound Caller"
          subtitle="Settings"
          backgroundColor="#fff"
          // Back hamesha isi agent ke home pe — footer ke Home tab jaisa hi.
          onBack={() => navigate("/fast-agent-detail", { state: { agentId, isPounce: true } })}
        />
      </div>

      <div className={styles.content}>
        <p className={styles.intro}>Manage how Outbound Caller captures, qualifies, and contacts leads.</p>

        {/* Status card — Live pe halka hara, Paused pe grey. */}
        <div className={`${styles.statusCard} ${live ? styles.statusLive : styles.statusPaused}`}>
          <span className={styles.text}>
            <span className={styles.rowTitle}>Outbound Call Status</span>
            {enabled !== null && (
              <span className={`${styles.state} ${live ? styles.stateLive : styles.statePaused}`}>
                {live ? "Live" : "Paused"}
              </span>
            )}
            <span className={styles.rowDesc}>
              {enabled === null
                ? "Checking status…"
                : live
                  ? "Eligible leads are being contacted automatically."
                  : "Calling is paused. New leads wait until you turn it back on."}
            </span>
          </span>
          <PounceToggle />
        </div>

        <div className={styles.sectionLabel}>Manage</div>
        <div className={styles.group}>
          {rows.map(({ key, title, desc, Icon, tone, allowed, onClick }) => (
            <button
              key={key}
              type="button"
              className={styles.row}
              disabled={!allowed}
              title={allowed ? undefined : POUNCE_LOCK_MESSAGE}
              onClick={onClick}
            >
              <span className={`${styles.icon} ${tone}`} aria-hidden="true">
                <Icon />
              </span>
              <span className={styles.text}>
                <span className={styles.rowTitle}>{title}</span>
                <span className={styles.rowDesc}>{desc}</span>
              </span>
              <Chevron />
            </button>
          ))}
        </div>
      </div>

      <PounceConnectorsModal isOpen={showSources} onClose={() => setShowSources(false)} />
      <PounceFooterNav agentId={agentId} active="settings" />
    </div>
  );
};

export default OutboundSettings;
